import React, { useEffect, useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Rating,
  Paper,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { getReviewsByCustomerId, getReviewsByTenantId } from "./TenancyApi";

// Best-effort date formatting — tolerates missing/invalid dates gracefully.
function formatReviewDate(row) {
  const raw = row.createdAt || row.created_at;
  if (!raw) return "—";
  const d = new Date(raw);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
}

/**
 * CustomerReviewModal — read-only dialog listing reviews in a table.
 *
 * Pass EITHER customerId (one guest's all-time reviews) OR tenantId
 * (every review tied to a tenancy). customerId takes priority if both
 * are given.
 *
 * Props:
 *   open        — boolean
 *   onClose()   — close handler
 *   customerId  — optional, fetches via getReviewsByCustomerId
 *   tenantId    — optional, fetches via getReviewsByTenantId
 *   title       — optional dialog title override
 *
 * NOTE: view-only. The original version's Edit button opened AddReview in
 * a state that was never actually wired to prefill the selected review's
 * values, so it's dropped here rather than kept half-working. Add real
 * edit support once AddReview.js can load an existing review's data.
 */
export default function CustomerReviewModal({ open, onClose, customerId, tenantId, title }) {
  const [tableData, setTableData] = useState([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!open) return

    const fetchReviews = async () => {
      setLoading(true)
      try {
        let data = []
        if (customerId) {
          data = await getReviewsByCustomerId(customerId)
        } else if (tenantId) {
          data = await getReviewsByTenantId(tenantId)
        }
        setTableData(data || [])
      } catch (error) {
        console.log(error)
        setTableData([])
      } finally {
        setLoading(false)
      }
    }

    fetchReviews()
  }, [open, customerId, tenantId])

  const dialogTitle = title || (customerId ? "Guest Reviews" : "Tenancy Reviews")

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      sx={{ zIndex: 2000 }}
    >
      <DialogTitle>{dialogTitle}</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        ) : tableData.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 2, textAlign: "center" }}>
            No reviews yet.
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  {/* <TableCell align="center"><b>Date Added</b></TableCell> */}
                  {!customerId && <TableCell align="center"><b>Guest</b></TableCell>}
                  <TableCell align="center"><b>Overall Rating</b></TableCell>
                  <TableCell align="center"><b>Comment</b></TableCell>
                  <TableCell align="center"><b>Noisy</b></TableCell>
                  <TableCell align="center"><b>Cleanliness</b></TableCell>
                  <TableCell align="center"><b>Pay On Time</b></TableCell>
                  <TableCell align="center"><b>Traffic</b></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {tableData.map((row, idx) => (
                  <TableRow key={row.id || row.reviewId || idx}>
                    {/* <TableCell align="center">
                      <Typography fontSize={13} color="text.secondary">
                        {formatReviewDate(row)}
                      </Typography>
                    </TableCell> */}
                    {!customerId && (
                      <TableCell align="center">
                        {row.fname || row.lname
                          ? `${row.fname || ""} ${row.lname || ""}`.trim()
                          : "—"}
                      </TableCell>
                    )}
                    <TableCell align="center">
                      <Rating value={row.overallRating || 0} readOnly />
                    </TableCell>
                    <TableCell align="center">{row.managerComments}</TableCell>
                    <TableCell align="center">
                      <Rating value={row.noisy || 0} readOnly />
                    </TableCell>
                    <TableCell align="center">
                      <Rating value={row.cleanliness || 0} readOnly />
                    </TableCell>
                    <TableCell align="center">
                      <Rating value={row.payOnTime || 0} readOnly />
                    </TableCell>
                    <TableCell align="center">
                      <Rating value={row.traffic || 0} readOnly />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant="contained" color="primary">
          Back
        </Button>
      </DialogActions>
    </Dialog>
  )
}